import { exportBackup, markBackupExported, todayYmd } from "../db";
import type {
  AppSettings,
  DiagnosisBlock,
  LearningStep,
  ReadingRecord,
  SelectionAnnotation,
  UserLevelProfile,
} from "../types";
import { downloadBytes, ensureXlsx } from "./loader";

export function settingsRows(settings: AppSettings, exportedAt: string) {
  return [
    { 项: "导出时间", 值: exportedAt },
    { 项: "默认英语水平", 值: settings.level },
    { 项: "AI 接口", 值: settings.ollamaApiUrl },
    { 项: "AI 模型", 值: settings.ollamaModel },
    { 项: "上次备份", 值: settings.lastBackupAt ?? "" },
  ];
}

function userLevelRows(profile: UserLevelProfile) {
  return [
    { 项: "评估水平", 值: profile.assessedLevel },
    { 项: "评估说明", 值: profile.levelReason ?? "" },
    { 项: "置信度", 值: profile.confidence },
    { 项: "上次评估时间", 值: profile.lastAssessedAt ?? "" },
    { 项: "手动覆盖", 值: profile.manualOverride ?? "" },
    { 项: "诊断完成数", 值: profile.signals.diagnosesCompleted },
    { 项: "步骤完成数", 值: profile.signals.stepsCompleted },
    { 项: "求详解次数", 值: profile.signals.simplifyRequests },
    { 项: "小测答题数", 值: profile.signals.checkQuestionsAnswered },
    { 项: "小测正确数", 值: profile.signals.checkQuestionsCorrect },
    { 项: "水平历史", 值: JSON.stringify(profile.levelHistory ?? []) },
  ];
}

function readingRows(readings: ReadingRecord[]) {
  return readings.map((r) => ({
    id: r.id,
    标题: r.title,
    原文: r.text,
    选中段落: r.selection ?? "",
    水平: r.level,
    诊断摘要: r.diagnosisSummary ?? "",
    原文译文: r.textTranslation ?? "",
    状态: r.status,
    当前步骤: r.currentStep,
    创建时间: r.createdAt,
    更新时间: r.updatedAt,
  }));
}

function diagnosisRows(blocks: DiagnosisBlock[]) {
  return blocks.map((b) => ({
    id: b.id,
    阅读id: b.readingId,
    类型: b.type,
    原文片段: b.excerpt,
    译文: b.translation ?? "",
    说明: b.note,
    排序: b.sort,
  }));
}

function stepRows(steps: LearningStep[]) {
  return steps.map((s) => ({
    id: s.id,
    阅读id: s.readingId,
    步骤序号: s.stepIndex,
    标题: s.title,
    内容: s.content,
    小测题: s.checkQuestion ?? "",
    是否完成: s.done ? "是" : "否",
    完成时间: s.completedAt ?? "",
  }));
}

export function annotationRows(annotations: SelectionAnnotation[]) {
  return annotations.map((a) => ({
    id: a.id,
    阅读id: a.readingId,
    选中文本: a.selectedText,
    回答: a.answer,
    创建时间: a.createdAt,
    更新时间: a.updatedAt,
  }));
}

export async function downloadExcelBackup(): Promise<void> {
  const data = await exportBackup();
  const XLSX = await ensureXlsx();
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(settingsRows(data.settings, data.exportedAt)), "设置");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userLevelRows(data.userLevelProfile)), "用户水平");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(readingRows(data.readings)), "阅读记录");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(diagnosisRows(data.diagnosisBlocks)), "诊断块");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stepRows(data.learningSteps)), "学习步骤");
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(annotationRows(data.selectionAnnotations ?? [])),
    "划词笔记",
  );

  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  downloadBytes(`readthrough-backup-${todayYmd()}.xlsx`, out);
  await markBackupExported(data.exportedAt);
}
